import { z } from "zod";

import { baseEntityFields, customFieldsField } from "./base.js";

/**
 * Consent : famille "consentement horodaté".
 *
 * Autorisation ou refus explicite donné par un contact pour un usage précis
 * (données, image, sortie, soins), horodaté et adossé à une preuve. Construite
 * sur le modèle "famille + type" de la bible (patron : `asset`).
 *
 * Le socle ne connaît pas la nature métier concrète du consentement ("droit à
 * l'image", "newsletter", "sortie scolaire") : il connaît la famille générique
 * `consent`. Le vocabulaire métier est porté par le discriminant `type` (chaîne
 * libre déclarée par l'app), et l'usage visé par le champ `scope`. Le contact
 * qui consent (`contactId`) et cet usage (`scope`) sont le CŒUR de la famille,
 * donc requis.
 *
 * Références vers d'autres familles = de simples ids (aucun import de schéma) :
 * `contactId` (cœur, requis), puis en optionnel `proofDocumentId` (pièce de
 * preuve, référence `document`), `relationshipId` (référence `relationship`),
 * `campaignId` (référence `campaign`), `messageId` (référence `message`).
 *
 * Sécurité / RGPD : le consentement vaut preuve, il est donc append-only.
 * Création autorisée, puis SEUL `revokedAt` peut être renseigné après coup (la
 * révocation) : aucun autre champ n'est modifiable et la suppression est
 * interdite. Cette contrainte est portée par les Security Rules (le SDK n'expose
 * d'ailleurs pas d'update en v0). En v0 la famille reste org-wide (même doctrine
 * qu'asset) ; la lecture fine par rôle viendra après, hors de ce schéma. Aucun
 * champ sensible au niveau document : le détail éventuel vit dans la pièce de
 * preuve pointée par `proofDocumentId`.
 *
 * Convention de patron (cf. asset) :
 *   - `xCoreFields`  : les champs MÉTIER seuls (sans les champs standard).
 *   - `XSchema`      : famille complète = champs standard + champs métier.
 *   - `XCreateInputSchema` : ce que l'appelant fournit à la création, VALIDÉ
 *     AVANT toute écriture Firestore.
 */

/** Champs métier de la famille consent (hors champs standard du socle). */
export const consentCoreFields = {
  orgId: z.string(),
  /** Discriminant de sous-type déclaré par l'app (ex. "image", "data", "sortie"). */
  type: z.string().min(1),
  /** Contact qui donne ou refuse le consentement (référence `contact`, cœur de la famille). */
  contactId: z.string().min(1),
  /** Usage précis visé, chaîne libre pilotée par l'app (ex. "image", "newsletter", "soins"). Cœur de la famille. */
  scope: z.string().min(1),
  /** Date de recueil du consentement (horodatage métier). Optionnel. */
  givenAt: z.date().optional(),
  /** Date de révocation ; SEUL champ renseignable après coup (append-only, cf. Security Rules). Optionnel. */
  revokedAt: z.date().optional(),
  /** Version du texte de consentement accepté (ex. "cgu-2026-01"). Optionnel. */
  textVersion: z.string().min(1).optional(),
  /** Pièce justificative du consentement (référence `document`). Optionnel. */
  proofDocumentId: z.string().min(1).optional(),
  /** Canal de recueil, chaîne libre pilotée par l'app (ex. "email", "papier", "guichet"). Optionnel. */
  channel: z.string().min(1).optional(),
  /** Relation liée (référence `relationship`). Optionnel. */
  relationshipId: z.string().min(1).optional(),
  /** Campagne à l'origine du recueil (référence `campaign`). Optionnel. */
  campaignId: z.string().min(1).optional(),
  /** Message à l'origine du recueil (référence `message`). Optionnel. */
  messageId: z.string().min(1).optional(),
} as const;

/** Schéma complet de la famille consent (champs standard + champs métier). */
export const ConsentSchema = z.object({
  ...baseEntityFields,
  ...consentCoreFields,
});

/** Type d'un consent. */
export type Consent = z.infer<typeof ConsentSchema>;

/**
 * Schéma des données fournies par l'appelant à la création.
 * Les champs standard (id, dates, auteurs, archivedAt, deletedAt, schemaVersion) sont
 * remplis par le SDK et ne sont donc PAS attendus ici.
 */
export const ConsentCreateInputSchema = z.object({
  ...consentCoreFields,
  ...customFieldsField,
});

/** Type de l'entrée de création d'un consent. */
export type ConsentCreateInput = z.infer<typeof ConsentCreateInputSchema>;

/**
 * Schema du patch de mise a jour (update partiel).
 * `orgId` et `type` sont immuables (Security Rules) ; les champs standard
 * sont geres par le SDK : aucun des deux n'est accepte ici.
 */
export const ConsentUpdateInputSchema = z
  .object({ ...consentCoreFields, ...customFieldsField })
  .omit({ orgId: true, type: true })
  .partial();

/** Type du patch de mise a jour d'un consent. */
export type ConsentUpdateInput = z.infer<typeof ConsentUpdateInputSchema>;
